import React, { useState, useEffect, useRef } from 'react';
import { 
  Folder, 
  FileText, 
   Box,
  MessageSquare, 
  Info, 
  Menu, 
  X, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2, XCircle, AlertTriangle, ShieldAlert,
   Send, Paperclip, Image, Film, File, Trash2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import api from '../../axios';
import { comment } from 'postcss';
import ApprovalModal from "./ApprovalModal";
import { Document, Page, pdfjs } from "react-pdf";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { IFCLoader } from "web-ifc-three";
import PdfViewer from "../components/PdfAnnotator";
import ModelPreloader from './ModelPreloader'; // Adjust path as needed
import { CSS2DRenderer, CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import PdfCDNViewer from "../components/PdfAnnotator"; // Adjust path as needed
import gsap from 'gsap';
import ProjectInfoModal from './ProjectInfoModal';



  const groupByType = (files) =>
  files.reduce((acc, file) => {
    if (!acc[file.type]) acc[file.type] = [];
    acc[file.type].push(file);
    return acc;
  }, {});
 

const CommentsSidebar = ({ isOpen, onClose, comments, setComments, id, hasPrivileges }) => {
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);



  
  // Handle adding a comment with attachments
  const handleAddComment = async (e) => {
    e.preventDefault();

   if(hasPrivileges == false){return}
  
    if (!newComment.trim() && attachments.length === 0) return;
  
    setIsUploading(true);
  
    try {
      const formData = new FormData();
      formData.append('text', newComment);
  
      attachments.forEach((file, index) => {
        formData.append(`attachment-${index}`, file);
      });
  
      const response = await api.post(`/projects/${id}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload Progress: ${percent}%`);
          setUploadProgress(percent);
          // You can also use setUploadProgress(percent) and show a progress bar
        }
      });
  
      setComments([...comments, response.data]);
      setNewComment('');
      setAttachments([]);
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsUploading(false);
    }
  };
  
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
    
    // Reset file input to allow selecting the same file again
    e.target.value = null;
  };
  
  // Handle removing an attachment
  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };
  
  // Get file type icon based on mimetype
  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return <Image size={16} />;
    if (file.type.startsWith('video/')) return <Film size={16} />;
    return <File size={16} />;
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: 350 }}
          animate={{ x: 0 }}
          exit={{ x: 350 }}
          transition={{ type: 'spring', damping: 30 }}
          className="bg-white border-l shadow-lg fixed right-0 top-0 bottom-0 z-40 w-80 flex flex-col"
        >
          <div className="p-4 flex justify-between items-center border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <MessageSquare className="text-blue-500" size={20} />
              <h3 className="font-medium">Comments</h3>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 p-1 rounded-full hover:bg-gray-100"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <div key={comment.id} className="bg-gray-50 p-3 rounded-lg shadow-sm">
                  <div className="flex justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                        {(comment.user?.name || "U").charAt(0)}
                      </div>
                      <p className="font-medium text-sm">{comment.user || "Unknown"}</p>
                    </div>
                    <p className="text-xs text-gray-500">{comment.timestamp || "Just now"}</p>
                  </div>
                  <p className="text-sm mb-2">{comment.text}</p>
                  
                  {/* Render attachments if any */}
                  {comment.attachments && comment.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {comment.attachments.map((attachment, i) => (
                        <div key={i} className="relative group">
                          {attachment.type.startsWith('image/') ? (
                            <div className="w-16 h-16 rounded overflow-hidden bg-gray-100">
                              <img 
                                src={attachment.url} 
                                alt={`Attachment ${i}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center">
                              {attachment.type.startsWith('video/') ? (
                                <Film className="text-gray-600" />
                              ) : (
                                <File className="text-gray-600" />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-4">
                <MessageSquare className="text-gray-300 mb-2" size={36} />
                <p className="text-gray-500 text-sm">No comments yet</p>
                <p className="text-gray-400 text-xs mt-1">Be the first to comment on this project</p>
              </div>
            )}
          </div>

          <div className="border-t p-3">
            {/* Display selected attachments */}
            {isUploading && uploadProgress !== null && (
              <div className="text-xs text-blue-500 mt-1">Uploading... {uploadProgress}%</div>
            )}
            {attachments.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {attachments.map((file, index) => (
                  <div 
                    key={index} 
                    className="bg-gray-100 rounded p-1 flex items-center gap-1 group"
                  >
                    {getFileIcon(file)}
                    <span className="text-xs truncate max-w-24">{file.name}</span>
                    <button 
                      onClick={() => removeAttachment(index)}
                      className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

          { hasPrivileges && (
            <form onSubmit={handleAddComment} className="flex flex-col">
              <div className="bg-gray-50 rounded-lg p-2 flex flex-col">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="bg-transparent resize-none outline-none text-sm min-h-12 max-h-32"
                  rows={1}
                />
                
                <div className="flex justify-between items-center mt-2">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      className="text-gray-500 hover:text-blue-500 p-1 rounded hover:bg-gray-100"
                      title="Attach files"
                    >
                      <Paperclip size={16} />
                    </button>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isUploading || (!newComment.trim() && attachments.length === 0)}
                    className={`p-2 rounded-full ${
                      isUploading || (!newComment.trim() && attachments.length === 0)
                        ? 'bg-gray-200 text-gray-400'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    } flex items-center justify-center`}
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </form>
          )}
            
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main Project Viewer Component
const ProjectReview = ({ projectId }) => {
 
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [isProjectInfoOpen, setIsProjectInfoOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isFileListOpen, setIsFileListOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pendingCommentPos, setPendingCommentPos] = useState(null); // world position
  const [commentTextInput, setCommentTextInput] = useState('');

  
  const { id } = useParams(); // Get project ID from URL
  const [activeFile, setActiveFile] = useState('');
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionType, setActionType] = useState(""); // 'approved' or 'rejecte
  const [user, setUser] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [ifcFiles, setIfcFiles] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(pdfFiles[0]);
  const [selectedIfc, setSelectedIfc] = useState(ifcFiles[0]);
  const ifcViewerContainerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const ifcLoaderRef = useRef(null);
  const [selectedComment, setSelectedComment] = useState(null); // for viewing pin click
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [projectStatus, setProjectStatus] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [hasPrivileges, setHasPrivileges] = useState(false);



  // const STORAGE_BASE_URL = "https://constructiontechpro.com/api/storage/";
  const STORAGE_BASE_URL = "https://constructiontechpro.com/api/storage/app/public/"; // Adjust this to your actual storage URL
  const [modelLoading, setModelLoading] = useState(false);
  
  const IFC_STORAGE_BASE_URL = "https://constructiontechpro.com/api/"; //

   
      
  // }, [activeFile, selectedIfc]);
  const ifcModelRef = useRef(null);
  const selectedPinRef = useRef(null);

  let currentSelection = null;
  const commentAnchors = useRef([]);


    // Fetch logged-in user
    useEffect(() => {
      const loggedInUser = JSON.parse(localStorage.getItem("user"));
      if (!loggedInUser?.id) {
        window.location.href = "/login"; // Redirect if not logged in
        return;
      }
      setUser(loggedInUser);
    }, []);

    
      // Fetch project details and comments
      useEffect(() => {
        const fetchProject = async () => {
        try {
            const response = await api.get(`/projects/${id}`);
            //console.log(response.data.pdf_files)
            setProject(response.data);
            const parsedDocs = typeof response.data.documents === 'string'
            ? JSON.parse(response.data.documents)
            : response.data.documents || {};

            setProjectStatus(response.data.status);
            console.log(response.data.status);
            console.log(user);
            if(user.role_id == response.data.stage || user.role_id == 'admin' || user.role_id == 'supervisor'){ 
              setHasPrivileges(true);
            }
          const pdfList = [];
          const ifcList = [];

          Object.entries(parsedDocs).forEach(([docType, versions]) => {
            versions.forEach((doc) => {
              const ext = doc.path.split('.').pop().toLowerCase();
              const name = `${docType.replace(/_/g, ' ')} (v${doc.version})`;

              const item = {
                id: `${docType}-v${doc.version}`,
                type: docType,
                name,
                version: doc.version,
                // path: IFC_STORAGE_BASE_URL,
                path: `${STORAGE_BASE_URL}${doc.path}`
              };

              if (ext === 'pdf') pdfList.push(item);
              if (ext === 'ifc') ifcList.push(item);
            });
          });



            setPdfFiles(pdfList);
            setIfcFiles(ifcList);

            console.log(pdfList);
            console.log(ifcList);

            if (pdfList.length > 0) {

              setSelectedPdf(pdfList[0]);
              setActiveFile('pdf');
              console.log(activeFile);
              console.log(pdfList[0]);

            } else if (ifcList.length > 0) {
              setSelectedIfc(ifcList[0]);
              setActiveFile('3d');
            }

        } catch (error) {
            console.error("Error fetching project:", error);
        } finally {
            setLoading(false);
        }
        };
        fetchProject();
    }, [id, user]);




    const addCommentPinToModel = (comment) => {
      //console.log(comment);
      const textureLoader = new THREE.TextureLoader();
      const pinTexture = textureLoader.load('/help.png');
    
      const material = new THREE.SpriteMaterial({ map: pinTexture, depthTest: false });
      const sprite = new THREE.Sprite(material);
    

      sprite.scale.set(0.01, 0.01, 0.01); // Start tiny
      sprite.position.set(...comment.position);
      sprite.userData.comment = comment;
    
      ifcModelRef.current?.add(sprite);
    
      // ✅ Animate the pin drop with ease-in bounce
      gsap.to(sprite.scale, {
        x: 3,
        y: 3,
        z: 3,
        duration: 0.70,
        ease: 'back.out(2)',
      });
    };
  
  
 
    const addCommentLabelToScene = (comment) => {
      const div = document.createElement('div');
      div.className =
        'bg-white bg-opacity-90 backdrop-blur text-sm rounded-lg shadow-xl p-3 border border-gray-200 max-w-[200px] transition-opacity duration-300';
      div.innerHTML = `
        <div class="text-gray-800 font-medium mb-1">💬 Comment</div>
        <div class="text-gray-600">${comment.text}</div>
      `;
    
      const anchor = new THREE.Object3D();
      anchor.position.set(...comment.position);
    
      const label = new CSS2DObject(div);
      label.position.set(0, 0, 0);
      anchor.add(label);
    
      ifcModelRef.current?.add(anchor);
    
      // Save it for animation visibility updates
      commentAnchors.current.push({ anchor, div });
    };
    
  
    
    useEffect(() => {
      if (activeFile !== '3d' || !ifcViewerContainerRef.current) return;
    
      if (sceneRef.current) {
        while (sceneRef.current.children.length > 0) {
          sceneRef.current.remove(sceneRef.current.children[0]);
        }
      }
    
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf0f0f0);
      sceneRef.current = scene;
    
      const size = {
        width: ifcViewerContainerRef.current.clientWidth,
        height: ifcViewerContainerRef.current.clientHeight,
      };

      const camera = new THREE.PerspectiveCamera(75, size.width / size.height, 0.1, 1000);
      camera.position.set(10, 10, 10);
      cameraRef.current = camera;

      /** ✅ Setup WebGL and CSS2D Renderers */
      const webglRenderer = new THREE.WebGLRenderer({ antialias: true });
      webglRenderer.setSize(size.width, size.height);
      webglRenderer.setPixelRatio(window.devicePixelRatio);

      const labelRenderer = new CSS2DRenderer();
      labelRenderer.setSize(size.width, size.height);
      labelRenderer.domElement.style.position = 'absolute';
      labelRenderer.domElement.style.top = '0';
      labelRenderer.domElement.style.pointerEvents = 'none';

      ifcViewerContainerRef.current.innerHTML = '';
      ifcViewerContainerRef.current.appendChild(webglRenderer.domElement);
      ifcViewerContainerRef.current.appendChild(labelRenderer.domElement);

      rendererRef.current = webglRenderer;


      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
      directionalLight.position.set(10, 20, 10);
      scene.add(directionalLight);
    
      const grid = new THREE.GridHelper(50, 50);
      scene.add(grid);
      const axes = new THREE.AxesHelper(10);
      scene.add(axes);
    
      // const controls = new OrbitControls(camera, renderer.domElement);
      const controls = new OrbitControls(camera, webglRenderer.domElement);
      controls.enableDamping = true;
    
      if (!ifcLoaderRef.current) {
        ifcLoaderRef.current = new IFCLoader();
        ifcLoaderRef.current.ifcManager.setWasmPath("/");
      }
    
      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();
    
      const handleDblClick = async (event) => {
        if (!ifcViewerContainerRef.current || !cameraRef.current || !rendererRef.current) return;
        
        //console.log('I am here');
        const bounds = rendererRef.current.domElement.getBoundingClientRect();
      
        const mouse = new THREE.Vector2();
        mouse.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
        mouse.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;

      
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, cameraRef.current);
      
        const intersects = raycaster.intersectObjects(sceneRef.current.children, true);
      
        if (intersects.length === 0) return;

        
        const intersect = intersects[0];
        //console.log(intersects);

        const modelID = intersect.object.parent?.modelID ?? intersect.object?.modelID;
        //console.log(modelID);
        if (modelID === undefined) {
          console.warn("Could not determine modelID from intersected object.");
          return;
        }
        if (modelID === undefined || modelID === null) return;


        
    
      const faceIndex = intersect.faceIndex;
      
        try {
          const expressID = await ifcLoaderRef.current.ifcManager.getExpressId(intersect.object.geometry, faceIndex);
          const worldPos = intersect.point.clone();
      
          setPendingCommentPos({
            modelID,
            expressID,
            position: intersect.point.clone().toArray(),
          });
          
          setShowCommentModal(true);
      
          // Save to localStorage
          const existing = JSON.parse(localStorage.getItem("ifc_comments")) || [];
          const updated = [...existing, newComment];
          localStorage.setItem("ifc_comments", JSON.stringify(updated));
      
          // Add label to scene
          addCommentLabelToScene(newComment);
      
        } catch (error) {
          console.error("Failed to get expressID or save comment:", error);
        }
      };
      
    
      const addCommentLabelToScene = (comment) => {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 64;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#000000';
        ctx.font = '20px Arial';
        ctx.fillText(comment.text, 10, 40);
        const texture = new THREE.CanvasTexture(canvas);
    
        const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.set(...comment.position);
        sprite.scale.set(2, 1, 1);
        scene.add(sprite);
      };
    
  
      const loadIFCModel = async () => {
        if (!selectedIfc) return;
    
        try {
          setModelLoading(true);
          const ifcModel = await ifcLoaderRef.current.loadAsync(selectedIfc.path);
          if (!ifcModel) return;
          ifcModelRef.current = ifcModel;

          scene.add(ifcModel);
    
          const box = new THREE.Box3().setFromObject(ifcModel);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const fov = camera.fov * (Math.PI / 180);
          const distance = maxDim / (2 * Math.tan(fov / 2));
    
          camera.position.set(center.x + distance, center.y + distance, center.z + distance);
          camera.lookAt(center);
          const minY = box.min.y;
          ifcModel.position.y -= minY;
          var cid = selectedIfc?.id || 0;


          

           const res = await api.get(`projects-ifc/fetch-comments?projectId=${id}&file_id=${cid}`);
           //console.log(res.data);
            res.data.forEach(c => {
              const parsed = {
                ...c,
                position: JSON.parse(c.position)
              };
              addCommentPinToModel(parsed);

          });
    
        

        


        } catch (error) {
          console.error("Error loading IFC file:", error);
        } finally {
          setModelLoading(false);
        }
      };
    
      loadIFCModel();
    
      const animate = () => {
        requestAnimationFrame(animate);
        
        controls.update();
        // ✅ Update label visibility based on distance
        commentAnchors.current.forEach(({ anchor, div }) => {
          const distance = camera.position.distanceTo(anchor.position);
          // div.style.opacity = distance < 20 ? "1" : "0"; // You can tweak this threshold
        });

        // renderer.render(scene, camera);
        webglRenderer.render(scene, camera);
        labelRenderer.render(scene, camera);

      };

      // ✅ Highlight material used for hover
      const highlightMaterial = new THREE.MeshBasicMaterial({
        color: 0x4f49f3,
        depthTest: false,
        transparent: true,
        opacity: 0.6,
      });

      const hoverRaycaster = new THREE.Raycaster();
      const hoverMouse = new THREE.Vector2();

      const handleMouseMove = async (event) => {
        if (!cameraRef.current || !rendererRef.current) return;

        const bounds = rendererRef.current.domElement.getBoundingClientRect();
        hoverMouse.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
        hoverMouse.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;

        hoverRaycaster.setFromCamera(hoverMouse, cameraRef.current);
        const intersects = hoverRaycaster.intersectObjects(sceneRef.current.children, true);

        if (intersects.length > 0 && ifcModelRef.current) {
          const intersect = intersects[0];
          const faceIndex = intersect.faceIndex;

          try {
            const id = await ifcLoaderRef.current.ifcManager.getExpressId(intersect.object.geometry, faceIndex);

            // Avoid flicker when hovering same element
            if (currentSelection !== id) {

        

              if (ifcModelRef.current && ifcModelRef.current?.modelID !== undefined) {
                ifcLoaderRef.current.ifcManager.removeSubset(
                  ifcModelRef.current?.modelID,
                  undefined,
                  sceneRef.current
                );
              }
              
              currentSelection = id;

              ifcLoaderRef.current.ifcManager.createSubset({
                modelID: ifcModelRef.current?.modelID,
                ids: [id],
                material: highlightMaterial,
                scene: sceneRef.current,
                removePrevious: true,
              });
            }

          } catch (err) {
            console.warn("Hover selection failed:", err);
          }
        } else {
          // Clear highlight
          ifcLoaderRef.current.ifcManager.removeSubset(
            ifcModelRef.current?.modelID,
            undefined,
            sceneRef.current
          );
          currentSelection = null;
        }

      };

      animate();
      
      const handlePinClick = (event) => {
        const bounds = rendererRef.current.domElement.getBoundingClientRect();
        const mouse = new THREE.Vector2(
          ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
          -((event.clientY - bounds.top) / bounds.height) * 2 + 1
        );
      
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, cameraRef.current);
        const intersects = raycaster.intersectObjects(sceneRef.current.children, true);
      
        for (const intersect of intersects) {
          if (intersect.object instanceof THREE.Sprite && intersect.object.userData.comment) {
            selectedPinRef.current = intersect.object;
            setSelectedComment(intersect.object.userData.comment);
            return;
          }
        }
      };
      
      


      window.addEventListener("dblclick", handleDblClick);
      const handleResize = () => {
        size.width = ifcViewerContainerRef.current.clientWidth;
        size.height = ifcViewerContainerRef.current.clientHeight;
        camera.aspect = size.width / size.height;
        camera.updateProjectionMatrix();
        // renderer.setSize(size.width, size.height);
        webglRenderer.setSize(size.width, size.height);
        labelRenderer.setSize(size.width, size.height);
      };
    
      window.addEventListener("resize", handleResize);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("click", handlePinClick);

    
      return () => {
        window.addEventListener("mousemove", handleMouseMove);
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("dblclick", handleDblClick);
        window.removeEventListener("click", handlePinClick);
        // renderer.dispose();
        webglRenderer.dispose();
      };

    }, [activeFile, selectedIfc]);


    useEffect(() => {
      const fetchComments = async () => {
        try {
          const response = await api.get(`/projects/${id}/comments`);
          console.log(response.data);
          setComments(response.data);
        } catch (error) {
          console.error('Failed to fetch comments:', error);
        }
      };
    
      if (id) fetchComments();
    }, [id]);


  
    const openModal = (type) => {
        setActionType(type);
        setModalOpen(true);
    };
    
    const closeModal = () => {
        setModalOpen(false);
        setActionType("");
    };




      if (loading) return <p className="text-gray-500 text-center">Loading project details...</p>;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Vertical Navigation Bar */}
      <motion.div 
        animate={{ width: isNavExpanded ? 240 : 80 }}
        className="bg-white border-r shadow-lg flex flex-col"
      >
        <div className="flex-1 flex flex-col items-center py-4 space-y-4">
          <button 
            onClick={() => setIsNavExpanded(!isNavExpanded)}
            className="p-2 hover:bg-gray-100 rounded"
          >
            {isNavExpanded ? <ChevronLeft /> : <ChevronRight />}
          </button>

          <button 
            onClick={() => {
              setIsFileListOpen(!isFileListOpen);
              setIsCommentsOpen(false);
              setIsProjectInfoOpen(false);
            }}
            className="p-2 flex flex-row hover:bg-gray-100 rounded"
          >
            <Folder />
            {isNavExpanded && <span className="ml-2">Documents</span>}
          </button>
          {hasPrivileges && (
              <>

                  <button 
                    onClick={() => {
                      setIsCommentsOpen(!isCommentsOpen);
                      setIsFileListOpen(false);
                      setIsProjectInfoOpen(false);
                    }}
                    className="p-2 flex flex-row hover:bg-gray-100 rounded"
                  >
                    <MessageSquare />
                    {isNavExpanded && <span className="ml-2">Comments</span>}
                  </button>
                  
                  <button 
                    onClick={() => {
                      setIsProjectInfoOpen(true);
                      setIsFileListOpen(false);
                      setIsCommentsOpen(false);
                    }}
                    className="p-2 flex flex-row hover:bg-gray-100 rounded"
                  >
                    <Info />
                    {isNavExpanded && <span className="ml-2">Project Info</span>}
                  </button>

              </>
          )}

        </div>
      </motion.div>

      {/* File List Sidebar */}
      <AnimatePresence>
        {isFileListOpen && (
            <motion.div
            initial={{ x: 320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 320, opacity: 0 }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed top-0 left-[80px] bottom-0 w-80 bg-white border-r shadow-lg z-40"
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Documents</h3>
              <button onClick={() => setIsFileListOpen(false)} className="text-gray-500 hover:text-gray-800">
                <X />
              </button>
            </div>
            <div className="p-4 space-y-2">
             

              {Object.entries(groupByType([...pdfFiles, ...ifcFiles])).map(([type, files]) => (
                <div key={type} className="mb-4">
                  <details className="group" >
                    <summary className="cursor-pointer flex items-center justify-between font-semibold text-gray-700 mb-1 px-1">
                      <span>{type.replace(/_/g, ' ')}</span>
                      <ChevronRight className="group-open:rotate-90 transition-transform h-4 w-4 text-gray-400" />
                    </summary>
                    <div className="pl-3 space-y-1 mt-2">
                      {files.map(file => (
                        <button
                          key={file.id}
                          onClick={() => {
                            if (file.path.endsWith('.pdf')) {
                              setSelectedIfc(null);
                              setSelectedPdf(file);
                              setActiveFile('pdf');
                              console.log(activeFile);
                            } else if (file.path.endsWith('.ifc')) {
                              setSelectedPdf(null);
                              setSelectedIfc(file);
                              setActiveFile('3d');
                              console.log(activeFile);
                            }
                            setIsFileListOpen(false);
                          }}
                          className={`w-full text-left flex items-center p-2 rounded-lg hover:bg-gray-100 transition ${
                            (selectedPdf?.id === file.id || selectedIfc?.id === file.id) ? 'bg-blue-100 text-blue-700' : ''
                          }`}
                        >
                          {file.path.endsWith('.pdf') ? <FileText className="w-4 h-4 mr-2" /> : <Box className="w-4 h-4 mr-2" />}
                          <span className="text-sm flex-1">{file.name}</span>
                          <span className="text-xs text-gray-400 ml-auto">v{file.version}</span>
                        </button>
                      ))}
                    </div>
                  </details>
                </div>
              ))}


            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Document Viewer */}
      <div className="flex-1 relative">
      
        {selectedPdf ? (
                activeFile === 'pdf' ? (
                  <>
                    <iframe
                      src={`/web/viewer.html?file=${encodeURIComponent(selectedPdf?.path)}&projectId=${id}`}
                      title="PDF.js Viewer"
                      width="100%"
                      height="800px"
                      style={{ border: 'none' }}
                    />
                       {/* <PdfCDNViewer url={selectedPdf?.path} /> */}
                  </>
                 
                ) : (
                  <>
                  <PdfCDNViewer url={selectedPdf?.path} />
                  </>
                
                )
        ) : (

          <div className="w-full h-full flex items-center justify-center bg-gray-50">
             <div style={{ width: '100%', height: '100%' }} className="flex-1 pl-4" ref={ifcViewerContainerRef} >
              <ModelPreloader isLoading={modelLoading} />
             </div>
          </div>
        )}
         
      </div>

      {/* Project Info Modal */}
      <ProjectInfoModal 
        isOpen={isProjectInfoOpen} 
        onClose={() => setIsProjectInfoOpen(false)} 
        project={project}
        onApprove={() => openModal("approved")}
        onReject={() => openModal("rejected")}
        statusType = {projectStatus}
        showAnimation = {showAnimation}
        setShowAnimation = {setShowAnimation}
        user = {user}
      />

      

      {/* Comments Sidebar */}
      <CommentsSidebar 
        isOpen={isCommentsOpen} 
        onClose={() => setIsCommentsOpen(false)} 
        comments={comments}
        setComments={setComments}
        id={id}
        hasPrivileges={hasPrivileges}
      />

        {/* Approval Modal */}
        <ApprovalModal
            isOpen={modalOpen}
            onClose={closeModal}
            projectId={id}
            actionType={actionType}
            statusType={projectStatus}
            refreshProject={() => window.location.reload()} // Replace with real function
      />

      {/* this is for comment on IFC modal */}
      <AnimatePresence>

      {showCommentModal && hasPrivileges && (
        <motion.div className="fixed inset-0 bg-black/50 z-[999] flex items-center justify-center">
          <motion.div className="bg-white p-6 rounded-xl w-96 shadow-2xl">
            <h2 className="text-xl font-semibold mb-4">Add Comment</h2>
            <textarea
              rows={4}
              className="w-full border rounded-lg p-2 text-sm"
              placeholder="Write something..."
              value={commentTextInput}
              onChange={(e) => setCommentTextInput(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowCommentModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>

            
            
              <button
                disabled={isSavingComment}
                onClick={async () => {
                  if (!commentTextInput.trim()) return;
                      setIsSavingComment(true);
                      try {
                        
                          const res = await api.post(`/projects-ifc/${id}/comments`, {

                            text: commentTextInput,
                            position: JSON.stringify(pendingCommentPos.position), // 🔧 convert to JSON string
                            express_id: pendingCommentPos.expressID,
                            file_id: selectedIfc?.id,

                          });

                          const newComment = {
                            ...res.data,
                            position: JSON.parse(res.data.position)
                          };

                          addCommentPinToModel(newComment);
                          setCommentTextInput('');
                          setShowCommentModal(false);
                      } catch (error) {
                        alert("Error saving comment: " + (error?.response?.data?.message || error.message));
                      } finally {
                        setIsSavingComment(false);
                      }
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              >
                {isSavingComment ? "Saving..." : "Add"}
              </button>

            </div>
          </motion.div>
        </motion.div>
       )}
    </AnimatePresence>
    
    <AnimatePresence>
      {selectedComment && (
        <motion.div className="fixed inset-0 bg-black/50 z-[999] flex items-center justify-center">
          <motion.div className="bg-white p-6 rounded-xl w-96 shadow-2xl">
            <h2 className="text-lg font-semibold text-pink-600 mb-2">📍 Comment</h2>
            <textarea
              className="w-full border rounded p-2 text-sm"
              value={selectedComment.text}
              onChange={(e) =>
                setSelectedComment({ ...selectedComment, text: e.target.value })
              }
              disabled={hasPrivileges ? false : true}
            />

            {hasPrivileges && (
              <div className="mt-4 flex justify-between">
               
                <button
                  onClick={ async () => {
                    // Remove pin from model
                    ifcModelRef.current?.remove(selectedPinRef.current);
                    await api.delete(`/projects-ifc/${id}/comments/${selectedComment.id}`);
                    // Remove from localStorage
                    const all = JSON.parse(localStorage.getItem("ifc_comments")) || [];
                    const updated = all.filter((c) => c.id !== selectedPinRef.current.userData.comment.id);
                    localStorage.setItem("ifc_comments", JSON.stringify(updated));

                    setSelectedComment(null);
                    selectedPinRef.current = null;
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  🗑 Delete
                </button>

                  <button

                      onClick={ async () => {

                            //Update localStorage
                            const all = JSON.parse(localStorage.getItem("ifc_comments")) || [];
                            const updated = all.map((c) =>
                              c.id === selectedComment.id ? { ...c, text: selectedComment.text } : c
                            );
                            localStorage.setItem("ifc_comments", JSON.stringify(updated));

                            const res = await api.put(`/projects-ifc/update-comment?projectId=${id}&commentId=${selectedComment.id}`, {
                              text: selectedComment.text,
                            });
                          
                            // Update label on pin
                            selectedPinRef.current.userData.comment = { ...selectedComment };

                            setSelectedComment(null);
                            selectedPinRef.current = null;

                      }}

                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Save
                  </button>
            
                  <div className="space-x-2">
                  <button
                    onClick={() => setSelectedComment(null)}
                    className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
                  >
                    Close
                  </button>

                   </div>
              </div>
            )}

        {!hasPrivileges && (
                      <div className="mt-4 flex justify-between">
                    

                          <button

                              onClick={ async () => {

                                    //Update localStorage
                                    const all = JSON.parse(localStorage.getItem("ifc_comments")) || [];
                                    const updated = all.map((c) =>
                                      c.id === selectedComment.id ? { ...c, text: selectedComment.text } : c
                                    );
                                    localStorage.setItem("ifc_comments", JSON.stringify(updated));

                                    const res = await api.put(`/projects-ifc/update-comment?projectId=${id}&commentId=${selectedComment.id}`, {
                                      text: selectedComment.text,
                                    });
                                  
                                    // Update label on pin
                                    selectedPinRef.current.userData.comment = { ...selectedComment };

                                    setSelectedComment(null);
                                    selectedPinRef.current = null;

                              }}

                            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                          >
                            Save
                          </button>
                    
                          
                      </div>
                  )}

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>



    </div>
  );
};

export default ProjectReview;